/**
 * Custom hook for initializing public recording session
 */

import {
  getOrCreateScribeJob,
  validateIntegrationKey,
} from '@/lib/public-recording-utils';
import { AuthStateSelector } from '@/store/selectors';
import type { PublicRecordingParams } from '@/types/public-recording';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export const usePublicRecordingInit = (params: PublicRecordingParams) => {
  const [isValidated, setIsValidated] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [scribeJobId, setScribeJobId] = useState<string | null>(null);
  const [gatewayToken, setGatewayToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Track if initialization has been attempted to prevent duplicate calls
  const hasInitialized = useRef(false);

  // Get partnerId from Redux store (integrationToken.partnerId or user.ev_integration_partner_id)
  const partnerIdFromStore = useSelector(AuthStateSelector.partnerId);

  const {
    appointmentId,
    patientId,
    locationId,
    sectionTag,
    integrationKey,
    partnerName,
    debugMode,
    patientFirstName,
    patientLastName,
    patientDob,
    patientCode,
  } = params;

  // Log initial parameters in debug mode
  useEffect(() => {
    if (debugMode) {
      console.log('[PublicAiScribe] Debug mode enabled');
      console.log('[PublicAiScribe] Query Parameters:', {
        appointmentId: appointmentId || '(missing)',
        patientId: patientId || '(missing)',
        locationId: locationId || '(missing)',
        sectionTag: sectionTag || '(missing)',
        partnerIdFromStore: partnerIdFromStore || '(missing)',
        partnerName: partnerName || '(missing)',
        integrationKey: integrationKey
          ? '***' + integrationKey.slice(-4)
          : '(missing)',
        patientFirstName: patientFirstName || '(missing)',
        patientLastName: patientLastName || '(missing)',
        patientDob: patientDob || '(missing)',
        patientCode: patientCode || '(missing)',
      });
    }
  }, [
    debugMode,
    appointmentId,
    patientId,
    locationId,
    sectionTag,
    partnerIdFromStore,
    partnerName,
    integrationKey,
    patientFirstName,
    patientLastName,
    patientDob,
    patientCode,
  ]);

  // Validate integration key and setup scribe job
  useEffect(() => {
    // Early return if partnerId is not yet available from store
    // This prevents the effect from running before authentication is complete
    // if (!partnerIdFromStore) {
    //   console.log('[PublicAiScribe] Waiting for partner ID from store...');
    //   return;
    // }

    // Prevent duplicate initialization (handles React 18 Strict Mode double render)
    if (hasInitialized.current) {
      console.log('[PublicAiScribe] Already initialized, skipping...');
      return;
    }

    const initialize = async () => {
      console.log('[PublicAiScribe] Initializing...');
      hasInitialized.current = true; // Mark as initialized
      setIsValidating(true);
      setError(null);

      // Validate required parameters
      const missingParams: string[] = [];
      if (!appointmentId) missingParams.push('appointmentId');
      if (!patientId) missingParams.push('patientId');
      if (!locationId) missingParams.push('locationId');
      if (!sectionTag) missingParams.push('sectionTag');
      if (!integrationKey) missingParams.push('integrationKey');
      if (!partnerName) missingParams.push('partnerName');
      if (!patientCode) missingParams.push('patientCode');

      if (missingParams.length > 0) {
        const errorMsg = `Missing required parameter${
          missingParams.length > 1 ? 's' : ''
        }: ${missingParams.join(', ')}`;
        console.error('[PublicAiScribe]', errorMsg);
        console.error(
          '[PublicRecording] Expected URL format: /ai-scribe?appointmentId=XXX&patientId=XXX&locationId=XXX&sectionTag=XXX&partnerName=XXX&integrationKey=XXX'
        );
        setError(errorMsg);
        setIsValidating(false);
        return;
      }

      console.log('[PublicAiScribe] All required parameters present');

      // Validate integration partner credentials using partnerId from store
      const validationResult = await validateIntegrationKey(
        partnerIdFromStore,
        integrationKey,
        partnerName
      );
      if (!validationResult.valid) {
        console.error(
          '[PublicAiScribe] Integration partner validation failed:',
          validationResult.error
        );
        setError(
          validationResult.error || 'Invalid partner ID or integration key'
        );
        setIsValidating(false);
        return;
      }

      if (!validationResult.token) {
        console.error(
          '[PublicAiScribe] Validation succeeded but no gateway token received'
        );
        setError('Authentication token not received from gateway');
        setIsValidating(false);
        return;
      }

      console.log(
        '[PublicAiScribe] Integration partner validated successfully, gateway token received'
      );
      setGatewayToken(validationResult.token);
      setIsValidated(true);

      // Get or create scribe job
      const result = await getOrCreateScribeJob(
        appointmentId,
        patientId,
        locationId,
        sectionTag,
        patientFirstName,
        patientLastName,
        patientDob
      );

      if (!result.jobId) {
        console.error(
          '[PublicAiScribe] Failed to setup scribe job:',
          result.error
        );
        setError(result.error || 'Failed to setup recording session');
        setIsValidating(false);
        return;
      }

      console.log(
        '[PublicAiScribe] ✅ Initialization complete. Scribe Job ID:',
        result.jobId
      );
      setScribeJobId(result.jobId);
      setIsValidating(false);
    };

    initialize();
  }, [
    appointmentId,
    patientId,
    locationId,
    sectionTag,
    integrationKey,
    partnerName,
    partnerIdFromStore,
    patientFirstName,
    patientLastName,
    patientDob,
    patientCode,
  ]);

  return {
    isValidated,
    isValidating,
    scribeJobId,
    gatewayToken,
    error,
  };
};
